import * as React from 'react'

import { cn } from '@/utilities/cn'

import styles from './glass.module.css'

export type GlassVariant = 'card' | 'sidebar' | 'inner'
export type GlassElevation = 'none' | 'flat' | 'raised' | 'floating'

export interface GlassSurfaceProps extends React.HTMLAttributes<HTMLDivElement> {
  /** which fill token to use — card (default), sidebar (denser), inner (nested) */
  variant?: GlassVariant
  /** grounding shadow depth */
  elevation?: GlassElevation
  /** hover fill change; enables the sheen if `sheen` is also set */
  interactive?: boolean
  /** render the travelling specular (only meaningful with `interactive`) */
  sheen?: boolean
  /**
   * Apply the backdrop-filter blur (default true). Set false for surfaces that
   * repeat per page — grid cards — so they stay within the runtime blur budget;
   * the glass read then comes from fill + border + edge alone (§2).
   */
  blur?: boolean
}

/**
 * The one local glass primitive (Phase 6). Replaces the `.glass-*` utilities.
 * Renders the four-layer glass recipe and lifts content internally, so callers
 * never need `relative z-30` on their content or a blanket `> *` rule.
 *
 * No 'use client': it has no hooks, so it composes into server components too;
 * event handlers passed by a caller make the caller the client boundary.
 */
export function GlassSurface({
  variant = 'card',
  elevation = 'raised',
  interactive = false,
  sheen = false,
  blur = true,
  className,
  children,
  ...rest
}: GlassSurfaceProps) {
  return (
    <div
      className={cn(
        styles.root,
        styles[variant],
        styles[elevation],
        interactive && styles.interactive,
        !blur && styles.noBlur,
        className,
      )}
      {...rest}
    >
      <span aria-hidden className={styles.overlay} />
      <span aria-hidden className={styles.edge} />
      {interactive && sheen && <span aria-hidden className={styles.sheen} />}
      <div className={styles.content}>{children}</div>
    </div>
  )
}
